import React, { useState } from 'react';
import Modal from '@mui/material/Modal';
import { Box, Button, FormControl, InputLabel, Input, FormHelperText, FormGroup, Select, MenuItem, CircularProgress, Typography } from '@mui/material';
import { Roles, AdminAccountCreateRequest, CustomerDTO } from '@beable/roster-backend-client-v1';
import adminManagementService from '../../../services/AdminManagementService';
import AdminValidation from '../../../interfaces/AdminValidation';
import validationService from '../../../services/ValidationService';
import { ModalCustomerSelector } from '../../Common/CustomerSelector';
import { useTheme } from '@mui/material/styles';
import { getStyles } from '../styles';

const defaultAdminValidation: AdminValidation = {
    username: "",
    password: "",
    role: "",
    customerId: "",
    customerName: "",
    email: ""
}

async function validateProfile(profile: AdminAccountCreateRequest) {
    const myValidation: AdminValidation = JSON.parse(JSON.stringify(defaultAdminValidation));
    const emailRegEx = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    //const pWordRegEx = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$^+=!*()@%&]).{8,10}$/g   //Pword policy: 1 capital, 1 lowercase, 1 special character, 1 number, between 8 and 10 characters
    const upperCaseRegEx = /[A-Z]/gm
    const usernameBlacklisted = ['!', '#', '$', '%', '^', '&', '*', '(', ')', '_', '-', '+', '=', '{', '}', '[', ']', '<', '>', '?', '/', '|', ',', ' ', '\\',];

    let isInvalid = false;
    if (!profile.username) {
        myValidation.username = 'Username is required'; isInvalid = true
    } else {
        //there is a username
        const firstChar = profile.username.charAt(0);
        const lastChar = profile.username.charAt(profile.username.length - 1);

        if (
            upperCaseRegEx.test(profile.username) ||
            usernameBlacklisted.some(v => profile.username.includes(v)) ||
            profile.username.includes('..') || 
            (profile.username.length < 4 || profile.username.length > 32) ||
            (firstChar === '@' || firstChar === '.') ||
            (lastChar === '@' || lastChar === '.')
        ) {
            myValidation.username = "Username must be [a-z,0-9,'@','.'].  Min: 4 Max: 32.  Begin and end with [a-z,0-9].";
            isInvalid = true;
        }
    }
    //if (!profile.password || !pWordRegEx.test(profile.password)) { myValidation.password = !pWordRegEx.test(profile.password) ? 'Invalid password, please check policy' : 'Password is required'; isInvalid = true }
    if (!profile.password) { myValidation.password = 'Password is required'; isInvalid = true }
    if (profile.password) {
        const validation = await validationService.getPasswordValidationError(profile.password);
        if (validation) {
            myValidation.password = validation;
            isInvalid = true;
        }
    }
    if (!profile.role) { myValidation.role = 'Invalid role'; isInvalid = true }
    if (!profile.email || !emailRegEx.test(profile.email)) { myValidation.email = 'Invalid email'; isInvalid = true }
    if (!profile.customerId || !profile.customerName) { myValidation.customerName = 'Invalid Customer'; isInvalid = true }
    return { myValidation, isInvalid };
}

export default function AddAdminModal(props: any) {
    const theme = useTheme();
    const styles = getStyles(theme);

    const [adminRole, setAdminRole] = useState(Roles.beableadmin);
    const [adminUsername, setAdminUsername] = useState("");
    const [adminPassword, setAdminPassword] = useState("");
    const [adminEmail, setAdminEmail] = useState("");
    const [adminCustomerId, setAdminCustomerId] = useState("");
    const [adminCustomerName, setAdminCustomerName] = useState("");
    const [validation, setValidation] = useState(defaultAdminValidation);
    const [isLoading, setIsLoading] = useState(false);
    const [hasErr, setErr] = useState(false);

    const resetState = () => {
        setAdminRole(Roles.beableadmin);
        setAdminUsername("");
        setAdminPassword("");
        setAdminEmail("");
        setAdminCustomerId("");
        setAdminCustomerName("");
        setValidation(defaultAdminValidation);
        setIsLoading(false);
        setErr(false);
    }

    const handleKeyPress = (event: any) => {
        if (event.key === 'Enter') {
            submitForm();
        }
    };

    const submitForm = async () => {
      const adminProfile: AdminAccountCreateRequest = {
        username: adminUsername,
        password: adminPassword,
        role: adminRole,
        email: adminEmail,
        customerId: adminCustomerId,
        customerName: adminCustomerName,
      };
      setErr(false);
      const validation = await validateProfile(adminProfile);
      if (validation.isInvalid) {
        setValidation(validation.myValidation);
      } else {
        setValidation(defaultAdminValidation);
        setIsLoading(true);
        const res = await adminManagementService.addNewAdmin(adminProfile);
        setIsLoading(false);
        if (res) {
          resetState();
          props.handleClose(true);
          setErr(false);
        }
        setErr(true);
      }
    };

    const handleAdminRoleChange = (e: any) => setAdminRole(e.target.value);
    const handleAdminPassChange = (e: any) => {
        if (!e.target.value.length) {
            setValidation(defaultAdminValidation);
        }
        setAdminPassword(e.target.value);
    }
    const handleAdminUsernameChange = (e: any) => {
        if (!e.target.value.length) {
            setValidation(defaultAdminValidation);
        }
        setAdminUsername(e.target.value);
    }
    const handleAdminEmailChange = (e: any) => {
        if (!e.target.value.length) {
            setValidation(defaultAdminValidation);
        }
        setAdminEmail(e.target.value.toLowerCase());
    }
    const handleAdminCustomerChange = (customer: CustomerDTO) => {
        setAdminCustomerId(customer?.id);
        setAdminCustomerName(customer?.name);
    }

    const passwordInputProps = {
        'data-testid': 'input-password',
      };
    
    return (
        <Modal
            aria-labelledby="admin-modal-title"
            aria-describedby="admin-modal-description"
            data-cy="create-new-admin-modal"
            open={props.open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    props.handleClose(event);
                }
            }}
            sx={{ overflow:'scroll' }}
            onKeyPress={handleKeyPress}
        >
            <Box sx={[styles.paper, { top: `26vh`, left: `34vw` }]}>
                <h2 id="admin-modal-title">Create new admin</h2>
                <p id="admin-modal-description">Use the form below to add an admin.</p>
                <FormGroup>
                    <FormControl variant="standard">
                        <InputLabel htmlFor="username" required>Admin's username</InputLabel>
                        <Input onChange={handleAdminUsernameChange} data-cy="username" id="username" aria-describedby="admin username" />
                        {validation.username !== '' ? <FormHelperText sx={styles.redTxt}>{validation.username}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard">
                        <InputLabel htmlFor="password" required>Admin's password</InputLabel>
                        <Input 
                            onChange={handleAdminPassChange} 
                            data-cy="password" 
                            inputProps={passwordInputProps}
                            id="password" 
                            aria-describedby="admin password" 
                            type="password" 
                        />
                        {validation.password !== '' ? <FormHelperText sx={styles.redTxt}>{validation.password}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard">
                        <ModalCustomerSelector
                            onSelect={handleAdminCustomerChange}
                            required
                        />
                        {validation.customerName ? <FormHelperText sx={styles.redTxt}>{validation.customerName}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard">
                        <InputLabel htmlFor="role" required>Admin's role</InputLabel>
                        <Select
                            variant="standard"
                            labelId="role"
                            id="role"
                            value={adminRole}
                            onChange={handleAdminRoleChange}>
                            <MenuItem value={Roles.beableadmin}>Beable administrator</MenuItem>
                            <MenuItem value={Roles.itadmin}>IT Admin</MenuItem>
                            <MenuItem value={Roles.districtadmin}>District administrator</MenuItem>
                            <MenuItem value={Roles.contentloader}>Content loader</MenuItem>
                            <MenuItem value={Roles.standardscorrelator}>Standards Correlator</MenuItem>
                        </Select>
                        {validation.role !== '' ? <FormHelperText sx={styles.redTxt}>{validation.role}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard">
                        <InputLabel htmlFor="email" required>Email address</InputLabel>
                        <Input onChange={handleAdminEmailChange} data-cy="email" id="email" aria-describedby="admin email" />
                        {validation.email !== '' ? <FormHelperText sx={styles.redTxt}>{validation.email}</FormHelperText> : undefined}
                    </FormControl>
                </FormGroup>
                <FormGroup>
                    {isLoading ? <CircularProgress color="primary" /> : undefined}
                    {hasErr ? <Typography sx={styles.redTxt}>Username duplicated!</Typography> : undefined}
                </FormGroup>
                <FormGroup sx={[styles.mt2, styles.dfr]}>
                    <Button data-cy="save-admin-button" variant="contained" color="primary" onClick={submitForm}>Save admin</Button>
                    <Button
                        data-cy="close-button"
                        variant="contained"
                        onClick={() => {
                            resetState();
                            props.handleClose();
                        }}
                        sx={styles.ml2}>Close</Button>
                </FormGroup>
            </Box>
        </Modal >
    );
}